import React, { ChangeEvent } from 'react';
import { Field } from 'formik';
import { FieldRenderProps } from '../types';
import { ToggleType } from './types';
import styled from 'styled-components/macro';
import { transitionEaseIn, transitionEaseOut } from 'V2/Shared/Button/styles';
import classNames from 'classnames';

export const Toggles = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

export const ToggleWrapper = styled.div`
  flex: 1 1 0;
  position: relative;
  margin-right: 12px;

  & > input {
    position: absolute;
    opacity: 0;
    height: 0;
    width: 0;
  }

  &:last-child {
    margin-right: 0;
  }

  & > label {
    display: block;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 48px;
    background-color: var(--toggle-label-bg);
    border-radius: var(--border-radius);
    cursor: pointer;
    user-select: none;
    ${transitionEaseIn};
    box-shadow: 0px 1px 0px 0px var(--toggle-label-shadow);
  }

  & > input + label > span {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 16px;
    font-size: 14px;
    text-transform: var(--button-text-transform);
    cursor: pointer;
    font-family: var(--font-family__secondary);
    font-weight: 500;
    text-align: center;
    height: 100%;
    width: 100%;

    color: var(--toggle-label-color);

    background-color: var(--button-toggle__bg);
    border: var(--button-toggle__border);
    border-radius: var(--border-radius);

    ${transitionEaseIn};
    transform: translateY(-1px);
  }

  & > input + label:hover {
    background-color: var(--toggle-label--hover-bg);
    ${transitionEaseOut};
    & > span {
      border: 1px solid var(--toggle-label--hover-border-color);
      box-shadow: 0px 1px 0px 0px var(--toggle-label--hover-shadow);
      background-color: var(--toggle-label--hover-bg);
      transform: translateY(-1px);
      ${transitionEaseOut};
    }
  }

  & > input:checked + label {
    background-color: var(--toggle-input--checked-bg);
    ${transitionEaseOut};
    & > span {
      border: 1px solid var(--toggle-label--checked-border-color);
      box-shadow: 0px 1px 0px 0px var(--toggle-label--checked-shadow);
      background-color: var(--toggle-label--checked-bg);
      transform: translateY(-1px);
      ${transitionEaseOut};
    }
  }

  & > input:focus + label {
    background-color: var(--toggle-input--focus__label-bg);
    outline: 2px solid var(--toggle-input--focus__label-outline-color);
    outline-offset: 4px;

    & > span {
      border: 1px solid var(--toggle-input--focus__label__span-border-color);
      background-color: var(--toggle-input--focus__label__span-bg);
      transform: translateY(-1px);
    }
  }

  & > input:focus:not(:focus-visible) + label {
    box-shadow: none;
    outline: none;
    ${transitionEaseOut};

    & > span {
      border: 1px solid
        var(--toggle-input--focus--not-focus-visible__label__span-border-color);
      background-color: var(
        --toggle-input--focus--not-focus-visible__label__span-bg
      );
      transform: translateY(-1px);
      ${transitionEaseOut};
    }
  }

  & > input:active:not(:focus-visible) + label {
    & > span {
      border: 1px solid
        var(--toggle-input--active--not-focus-visible__label__span-border-color);
      background-color: var(
        --toggle-input--active--not-focus-visible__label__span-bg
      );
      transform: translateY(0px);
      ${transitionEaseOut};
    }
  }

  & > input:disabled + label {
    cursor: not-allowed;
    background-color: var(--gray-10);
    box-shadow: none;

    & > span {
      cursor: not-allowed;
      color: var(--gray-50);
      background-color: var(--gray-10);
      border: 1px solid var(--gray-30);
    }
  }
`;

export const Toggle: React.FC<ToggleType> = ({
  options,
  name,
  dataTestId,
  onChange,
  className,
  ...inputProps
}) => (
  <Field name={name}>
    {({ field }: FieldRenderProps) => (
      <Toggles
        className={classNames('cc-toggle', className)}
        data-testid="toggle"
      >
        {options.map((option, i) => (
          <ToggleWrapper key={i} data-testid={`${name}-${option.value}`}>
            <input
              {...field}
              {...inputProps}
              className="cc-toggle__input"
              data-testid={
                dataTestId
                  ? `${dataTestId}-${option.value}`
                  : `input-${option.value}`
              }
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (onChange) {
                  onChange(event);
                }

                field.onChange(event);
              }}
              type="radio"
              name={name}
              value={`${option.value}`}
              id={`${name}-${option.value}`}
              checked={field.value.toString() === String(option.value)}
            />
            <label
              className="cc-toggle__label"
              htmlFor={`${name}-${option.value}`}
              data-testid="label"
            >
              <span className="cc-toggle__label__span">{option.label}</span>
            </label>
          </ToggleWrapper>
        ))}
      </Toggles>
    )}
  </Field>
);
